/* global.js — drawers, focus management, disclosure menus, shared utilities */
(function () {
  'use strict';
  const theme = (window.theme = window.theme || {});

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ---------- Focus trap ---------- */
  theme.trapFocus = function (container) {
    container.__lastFocused = document.activeElement;
    const getNodes = () =>
      Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);
    container.__trap = function (e) {
      if (e.key !== 'Tab') return;
      const nodes = getNodes();
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', container.__trap);
    const nodes = getNodes();
    if (nodes.length) nodes[0].focus();
  };

  theme.releaseFocus = function (container) {
    if (container.__trap) {
      container.removeEventListener('keydown', container.__trap);
      delete container.__trap;
    }
    if (container.__lastFocused && document.contains(container.__lastFocused)) {
      container.__lastFocused.focus();
    }
    delete container.__lastFocused;
  };

  /* ---------- Drawers ---------- */
  const overlay = () => document.querySelector('[data-overlay]');
  let openDrawerId = null;

  theme.openDrawer = function (id) {
    const el = document.getElementById(id);
    if (!el || openDrawerId === id) return;
    if (openDrawerId) theme.closeDrawer(openDrawerId);
    // cancel any pending "hide after close animation" from a previous close
    if (el.__closeEnd) {
      el.removeEventListener('transitionend', el.__closeEnd);
      delete el.__closeEnd;
    }
    el.removeAttribute('hidden');
    // force a frame so the transition runs
    requestAnimationFrame(() => el.classList.add('is-open'));
    document.body.classList.add('scroll-locked');
    const ov = overlay();
    if (ov) ov.removeAttribute('hidden');
    openDrawerId = id;
    theme.trapFocus(el);
    document.dispatchEvent(new CustomEvent('drawer:open', { detail: { id } }));
  };

  theme.closeDrawer = function (id) {
    const el = document.getElementById(id);
    if (!el) return;
    el.classList.remove('is-open');
    document.body.classList.remove('scroll-locked');
    const ov = overlay();
    if (ov) ov.setAttribute('hidden', '');
    const onEnd = (e) => {
      // only react to the drawer's own slide transition — never to bubbled
      // transitions from elements inside it (inputs, buttons, etc.)
      if (e && (e.target !== el || e.propertyName !== 'transform')) return;
      el.setAttribute('hidden', '');
      el.removeEventListener('transitionend', onEnd);
      delete el.__closeEnd;
    };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      onEnd();
    } else {
      el.__closeEnd = onEnd;
      el.addEventListener('transitionend', onEnd);
    }
    openDrawerId = null;
    theme.releaseFocus(el);
    document.dispatchEvent(new CustomEvent('drawer:close', { detail: { id } }));
  };

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      theme.openDrawer(opener.getAttribute('data-drawer-open'));
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      e.preventDefault();
      const drawer = closer.closest('.drawer');
      if (drawer) theme.closeDrawer(drawer.id);
      return;
    }
    if (e.target.closest('[data-overlay]') && openDrawerId) {
      theme.closeDrawer(openDrawerId);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && openDrawerId) theme.closeDrawer(openDrawerId);
  });

  /* ---------- Details disclosures (header dropdowns): close on outside click / Escape ---------- */
  document.addEventListener('click', (e) => {
    document.querySelectorAll('details[data-disclosure][open]').forEach((d) => {
      if (!d.contains(e.target)) d.removeAttribute('open');
    });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('details[data-disclosure][open]').forEach((d) => {
      d.removeAttribute('open');
      const summary = d.querySelector('summary');
      if (summary) summary.focus();
    });
  });

  /* ---------- Quantity steppers (event delegation; used in PDP + cart) ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    const wrapper = btn.closest('[data-qty]');
    const input = wrapper && wrapper.querySelector('input[type="number"]');
    if (!input) return;
    const step = btn.getAttribute('data-qty-change') === 'up' ? 1 : -1;
    const min = parseInt(input.min || '1', 10);
    const next = Math.max(min, (parseInt(input.value, 10) || min) + step);
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Announcement bar dismiss ---------- */
  document.addEventListener('click', (e) => {
    const dismiss = e.target.closest('[data-announcement-dismiss]');
    if (!dismiss) return;
    const bar = dismiss.closest('.announcement-bar');
    if (bar) bar.remove();
    try {
      sessionStorage.setItem('announcement-dismissed', '1');
    } catch (err) {
      /* private mode */
    }
  });
  try {
    if (sessionStorage.getItem('announcement-dismissed') === '1') {
      const bar = document.querySelector('.announcement-bar');
      if (bar) bar.remove();
    }
  } catch (err) {
    /* private mode */
  }

  /* ---------- "Read more" toggles ---------- */
  document.addEventListener('click', (e) => {
    const toggle = e.target.closest('[data-read-more]');
    if (!toggle) return;
    const target = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!target) return;
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    target.classList.toggle('is-clamped', expanded);
    toggle.textContent = expanded ? toggle.getAttribute('data-more-label') : toggle.getAttribute('data-less-label');
  });
})();